import * as Sentry from "@sentry/react";
import { env } from "./env";
import { hasFullConsent, subscribeToCookieConsent } from "./cookie-consent";

/**
 * Errors we know are not ours - browser-extension noise, third-party SDK
 * transient failures, and common benign warnings. Sentry drops any event
 * whose message matches one of these strings / patterns before it leaves
 * the browser, so we save quota and keep the Issues tab signal-to-noise
 * ratio high.
 *
 * Add cautiously: if we ever see a real bug that sneaks past one of these
 * patterns we'd debug blind. Each entry below should have a comment
 * explaining WHY it's noise.
 */
const IGNORED_ERRORS: (string | RegExp)[] = [
  // Microsoft Editor / "Selection Tools" / similar browser extensions
  // inject a messaging script that rejects on every page. No stacktrace,
  // not from our bundle. Two users on our site hit this in the first 24h.
  "Object Not Found Matching Id",
  /MethodName:update.*ParamCount/,
  // Generic "Non-Error promise rejection captured" with no useful payload
  // - almost always an extension wrapping a string/object into a
  // rejection. Real code-bug rejections have stack traces and we keep
  // those.
  /Non-Error promise rejection captured with value: Object/,
  // Common cross-origin script errors with no details (CORS-blocked
  // extensions, ad-blockers).
  "Script error.",
  // ResizeObserver loop warnings - browser-emitted, not a real error.
  // Fires at the tail end of layout animations, benign.
  /ResizeObserver loop/,
];

/**
 * Initialise Sentry as early as possible in app boot, before React renders so
 * that render errors in the tree are captured. Safe to call when no DSN is
 * configured (local dev); in that case Sentry is disabled but the rest of the
 * app continues to work.
 *
 * Session Replay is gated on cookie consent: error capture + performance
 * tracing are always on (they don't record user content and are necessary
 * for service reliability), but Replay is only enabled if the user chose
 * "Accept all" on the banner. Once the user makes a choice we re-init
 * Sentry with the right integration set - no page reload required.
 */
export function initSentry() {
  if (!env.sentryDsn) {
    if (env.environment !== "development") {
      // Loudly warn outside dev. Missing DSN in staging/prod is a mistake.
      console.warn(
        "[sentry] no VITE_SENTRY_DSN configured, crash reporting disabled"
      );
    }
    return;
  }

  configureSentry();

  // Re-configure whenever the user changes their consent choice (banner
  // click, or another tab flipping the flag). init() is idempotent enough
  // that this is safe to call multiple times.
  subscribeToCookieConsent(() => configureSentry());
}

function configureSentry() {
  const integrations: Sentry.BrowserOptions["integrations"] = [
    Sentry.browserTracingIntegration(),
  ];

  if (hasFullConsent()) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      })
    );
  }

  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.environment,
    integrations,
    tracesSampleRate: env.environment === "production" ? 0.2 : 1.0,
    replaysSessionSampleRate:
      hasFullConsent() && env.environment === "production" ? 0.05 : 0,
    replaysOnErrorSampleRate: hasFullConsent() ? 1.0 : 0,
    sendDefaultPii: false,
    ignoreErrors: IGNORED_ERRORS,
  });
}

export { Sentry };
